import { useMemo } from 'react';
import { PricingPlan, LimitPeriod } from '@cyberlabrs/helpers';
import { useAppSelector } from '../../store';
import { selectFeatureConfig } from '../../store/slices/feature-config.slice';
import { selectActiveSubscription } from '../../store/slices/active-subscription.slice';
import { useFeatureAvailability } from './useFeatureAvailability';
import { ActiveSubscriptionResponseDto, FeaturePricingPlanName } from '../../commonTypes/organization.types';
import { useFeatureFlag } from '../featureFlags/useFeatureFlags';
import { AppConfig } from '../../config/AppConfig';

export type ItemWithId = {
    id: string;
    createdAt?: string | Date | null;
};

export type FeaturePlanLimitsResult = {
    canCreate: boolean;
    reason: string | null;
    limit: number | null;
    currentCount: number | null;
    period: LimitPeriod | null;
    disabledItemIds: Set<string>;
    isLimitReached: boolean;
    shouldShowPremiumTooltip: boolean;
    isDisabled: boolean;
    enabled: boolean;
};

type PlanLimitation = {
    enabled?: boolean;
    limit?: {
        value: number | boolean | null;
        period?: LimitPeriod;
    };
};

export const useFeaturePlanLimits = <T extends ItemWithId>(
    featureName: string,
    action: string,
    items: T[] = [],
    limitAction?: string,
    excludeFromLimit?: (item: T) => boolean
): FeaturePlanLimitsResult => {
    const featureAvailability = useFeatureAvailability(featureName);
    const configs = useAppSelector(selectFeatureConfig);
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const billingEnabled = useFeatureFlag('BILLING');
    const isDevelopment = AppConfig.isDevelopment;

    return useMemo(() => {
        if (!billingEnabled && !isDevelopment) {
            return {
                canCreate: true,
                reason: null,
                limit: null,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: null,
                disabledItemIds: new Set<string>(),
                isLimitReached: false,
                shouldShowPremiumTooltip: false,
                isDisabled: false,
                enabled: true,
            };
        }

        if (!featureAvailability.isAvailable) {
            return {
                canCreate: false,
                reason: featureAvailability.reason || 'FEATURE_DISABLED',
                limit: null,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: null,
                disabledItemIds: new Set(items.map(item => item.id)),
                isLimitReached: true,
                shouldShowPremiumTooltip: false,
                isDisabled: true,
                enabled: false,
            };
        }

        const cfg = configs.find(c => c.name === featureName);
        if (!cfg) {
            return {
                canCreate: false,
                reason: 'NO_CONFIG',
                limit: null,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: null,
                disabledItemIds: new Set<string>(),
                isLimitReached: false,
                shouldShowPremiumTooltip: false,
                isDisabled: true,
                enabled: false,
            };
        }

        const subscription = activeSubscription as ActiveSubscriptionResponseDto | null;
        const rawPlanName = subscription?.name?.toUpperCase() as FeaturePricingPlanName | undefined;
        const PLAN_ORDER: FeaturePricingPlanName[] = [PricingPlan.FREE, PricingPlan.PREMIUM, PricingPlan.ENTERPRISE, PricingPlan.LIFETIME];
        const currentPlan: FeaturePricingPlanName = rawPlanName && PLAN_ORDER.includes(rawPlanName) ? rawPlanName : PricingPlan.FREE;

        if (currentPlan === PricingPlan.LIFETIME) {
            return {
                canCreate: true,
                reason: null,
                limit: null,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: null,
                disabledItemIds: new Set<string>(),
                isLimitReached: false,
                shouldShowPremiumTooltip: false,
                isDisabled: false,
                enabled: true,
            };
        }

        const planConfig = cfg.config.subscription[currentPlan];

        // Check for create action limitation
        const createLimitation = planConfig?.limitations?.[action] as PlanLimitation | undefined;

        // Check for view/list limitation (to determine which items to disable)
        const limitActions = limitAction ? [limitAction] : ['view', 'list', 'access', 'manage'];
        let itemLimit: number | null = null;
        let limitPeriod: LimitPeriod | null = null;

        for (const limitActionName of limitActions) {
            const limitation = planConfig?.limitations?.[limitActionName] as PlanLimitation | undefined;

            if (limitation?.limit?.value && typeof limitation.limit.value === 'number') {
                itemLimit = limitation.limit.value;
                limitPeriod = limitation.limit.period || null;
                break;
            }
        }

        // If no specific limit action found, check if there's a general limit
        if (itemLimit === null && planConfig?.limitations) {
            const limitationValues = Object.values(planConfig.limitations) as PlanLimitation[];
            const generalLimit = limitationValues.find(lim => {
                return lim?.limit?.value !== undefined && typeof lim.limit.value === 'number';
            }) as
                | {
                      limit: {
                          value: number;
                          period?: LimitPeriod;
                      };
                  }
                | undefined;

            if (generalLimit) {
                itemLimit = generalLimit.limit.value;
                limitPeriod = generalLimit.limit.period || null;
            }
        }

        // Handle create limitation
        if (!createLimitation) {
            // No create limitation, but check item limit for disabling
            if (itemLimit === null) {
                return {
                    canCreate: true,
                    reason: null,
                    limit: null,
                    currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                    period: null,
                    disabledItemIds: new Set<string>(),
                    isLimitReached: false,
                    shouldShowPremiumTooltip: false,
                    isDisabled: false,
                    enabled: true,
                };
            }

            // Separate excluded items (always enabled) from items that count towards limit
            const excludedItems = excludeFromLimit ? items.filter(excludeFromLimit) : [];
            const itemsToCount = excludeFromLimit ? items.filter(item => !excludeFromLimit(item)) : items;

            // Determine which items should be disabled (only from items that count towards limit)
            const sortedItems = [...itemsToCount].sort((a, b) => {
                const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                if (aDate !== bDate) return aDate - bDate;
                // Fallback to ID comparison for consistent ordering
                return a.id.localeCompare(b.id);
            });

            const disabledItemIds = new Set<string>();
            if (sortedItems.length > itemLimit) {
                for (let i = itemLimit; i < sortedItems.length; i++) {
                    disabledItemIds.add(sortedItems[i].id);
                }
            }

            // Excluded items are never disabled
            excludedItems.forEach(item => {
                disabledItemIds.delete(item.id);
            });

            const isLimitReached = itemsToCount.length >= itemLimit;

            return {
                canCreate: !isLimitReached,
                reason: isLimitReached ? 'LIMIT_REACHED' : null,
                limit: itemLimit,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: limitPeriod,
                disabledItemIds,
                isLimitReached,
                shouldShowPremiumTooltip: isLimitReached && currentPlan === PricingPlan.FREE,
                isDisabled: false,
                enabled: true,
            };
        }

        // Check if create limitation is disabled
        if (createLimitation.enabled === false) {
            return {
                canCreate: false,
                reason: 'LIMITATION_DISABLED',
                limit: typeof createLimitation.limit?.value === 'number' ? createLimitation.limit.value : itemLimit,
                currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
                period: createLimitation.limit?.period || limitPeriod || null,
                disabledItemIds: new Set(items.map(item => item.id)),
                isLimitReached: true,
                shouldShowPremiumTooltip: currentPlan === PricingPlan.FREE,
                isDisabled: true,
                enabled: false,
            };
        }

        const createLimitValue = createLimitation.limit?.value;
        const createLimit = typeof createLimitValue === 'number' ? createLimitValue : null;

        // Use create limit if available, otherwise use item limit
        const effectiveLimit = createLimit !== null ? createLimit : itemLimit;
        const effectivePeriod = createLimitation.limit?.period || limitPeriod || null;

        // Separate excluded items (always enabled) from items that count towards limit
        const excludedItems = excludeFromLimit ? items.filter(excludeFromLimit) : [];
        const itemsToCount = excludeFromLimit ? items.filter(item => !excludeFromLimit(item)) : items;

        // Determine which items should be disabled (use item limit if different from create limit)
        const sortedItems = [...itemsToCount].sort((a, b) => {
            const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (aDate !== bDate) return aDate - bDate;
            return a.id.localeCompare(b.id);
        });

        const disabledItemIds = new Set<string>();
        const limitToUseForDisabling = itemLimit !== null ? itemLimit : effectiveLimit;

        if (limitToUseForDisabling !== null && sortedItems.length > limitToUseForDisabling) {
            for (let i = limitToUseForDisabling; i < sortedItems.length; i++) {
                disabledItemIds.add(sortedItems[i].id);
            }
        }

        // Excluded items are never disabled
        excludedItems.forEach(item => {
            disabledItemIds.delete(item.id);
        });

        const isLimitReached = effectiveLimit !== null && itemsToCount.length >= effectiveLimit;
        // At this point, enabled is either true or undefined (both mean enabled), since we already handled false case
        const canCreate = !isLimitReached;

        const enabled = true;
        const isDisabled = false;

        return {
            canCreate,
            reason: isLimitReached ? 'LIMIT_REACHED' : null,
            limit: effectiveLimit,
            currentCount: excludeFromLimit ? items.filter(item => !excludeFromLimit(item)).length : items.length,
            period: effectivePeriod,
            disabledItemIds,
            isLimitReached,
            shouldShowPremiumTooltip: isLimitReached && currentPlan === PricingPlan.FREE,
            isDisabled,
            enabled,
        };
    }, [featureAvailability, configs, activeSubscription, featureName, action, items, limitAction, excludeFromLimit, billingEnabled, isDevelopment]);
};

export type FeatureLimitation = {
    isDisabled: boolean;
    enabled: boolean;
};

export const useFeatureLimitation = (featureName: string, limitationKey: string): FeatureLimitation => {
    const result = useFeaturePlanLimits(featureName, limitationKey, []);
    return {
        isDisabled: result.isDisabled,
        enabled: result.enabled,
    };
};
